import SpreadsheetV16 from '@/components/UI/SpreadSheet/SpreadsheetV16';
import type { SpreadsheetV16Settings } from '@/components/UI/SpreadSheet/SpreadsheetV16.model';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTableV16';
import type { Animal } from '@/model/Animal.model';
import type { MetadataField } from '@/model/Metadata.model';
import type { BulkSamplesFormData, SampleData } from '@/model/Sample.model';
import { type FC, type MutableRefObject, useEffect, useMemo, useRef } from 'react';
import { _notNil } from '@/littledash';
import InVivoError from '@/model/InVivoError';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import {
  TimepointEditorV16,
  timepointAfterPasteHandlerV16,
} from '@/components/UI/SpreadSheet/editors-v16/TimepointEditorV16';
import { DateTimeEditorV16 } from '@/components/UI/SpreadSheet/editors-v16/DateTimeEditorV16';
import { defaultMetadataToColumnSettingMapperV16 } from '@/support/hot-v16';
import { generateColumnsV16, generateSampleDataV16 } from './BulkCreateSamples.utils';

TimepointEditorV16.register();
DateTimeEditorV16.register();

interface ViewBulkSampleDetailsTableV16Props {
  subjects: Array<Animal>;
  metadata: Array<MetadataField>;
  setInvalid: (invalid: boolean) => void;
  sampleDetails: BulkSamplesFormData;
  plannedSamplesEnabled: boolean;
  dataRef: MutableRefObject<Array<SampleData>>;
  onReady: (ready: boolean) => void;
}

const ViewBulkSampleDetailsTableV16: FC<ViewBulkSampleDetailsTableV16Props> = ({
  subjects,
  metadata,
  setInvalid,
  sampleDetails,
  plannedSamplesEnabled,
  dataRef,
  onReady,
}) => {
  const data = useMemo(
    () => generateSampleDataV16(subjects, metadata, sampleDetails),
    [subjects, metadata, sampleDetails]
  );
  dataRef.current = data;

  useEffect(() => {
    onReady(true);
    return () => {
      onReady(false);
    };
  }, [onReady]);

  const { colHeaders, columns } = useMemo(
    () => generateColumnsV16(sampleDetails, metadata, plannedSamplesEnabled),
    [sampleDetails, metadata, plannedSamplesEnabled]
  );
  const ref = useRef<HotTableRef>(null);

  useEffect(() => {
    const hot = ref.current?.hotInstance;
    if (_notNil(hot)) {
      hot.addHook('afterPaste', timepointAfterPasteHandlerV16);

      return () => {
        try {
          if (!hot.isDestroyed) {
            hot.removeHook('afterPaste', timepointAfterPasteHandlerV16);
          }
        } catch (error) {
          ExceptionHandler.captureException(
            new InVivoError('Failed to remove timepoint paste hook during cleanup', {
              cause: error,
              slug: 'view-bulk-sample-details-hot-cleanup-error-v16',
            })
          );
        }
      };
    }
  }, [ref]);

  const settings: SpreadsheetV16Settings = {
    className: 'htMiddle',
    colHeaders,
    columnSorting: false,
    columns,
    contextMenu: false,
    height: 500,
    hiddenColumns: { copyPasteEnabled: true, columns: [] },
    manualColumnResize: true,
    rowHeaders: true,
    rowHeights: 50,
    fixedColumnsStart: 3,
  };

  return (
    <SpreadsheetV16
      data={data}
      settings={settings}
      metadata={metadata}
      metadataToColumnSettingMapper={defaultMetadataToColumnSettingMapperV16}
      innerRef={ref}
      maxRows={subjects.length}
      addCol={false}
      setInvalid={setInvalid}
    />
  );
};

export default ViewBulkSampleDetailsTableV16;
